import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Typography, Table, TableHead, TableBody, TableRow, TableCell } from '@material-ui/core'

const useStyles = makeStyles(() => ({
  root: {
    border: '1px solid var(--color-neutral-200, #e5e7eb)',
    borderRadius: 6,
    background: 'var(--opal-surface-default, #fff)',
    overflow: 'hidden'
  },
  header: {
    padding: '12px 16px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    borderBottom: '1px solid var(--color-neutral-200, #e5e7eb)'
  },
  title: {
    fontSize: 14,
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-neutral-800, #1f2937)'
  },
  description: {
    fontSize: 12,
    color: 'var(--color-neutral-500, #6b7280)',
    lineHeight: 1.45
  },
  table: { width: '100%' },
  th: {
    fontSize: 11,
    fontWeight: 'var(--font-weight-semibold, 600)',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: 'var(--color-neutral-500, #6b7280)',
    padding: '8px 12px',
    background: 'var(--color-neutral-50, #f9fafb)',
    borderBottom: '1px solid var(--color-neutral-200, #e5e7eb)'
  },
  td: {
    fontSize: 13,
    padding: '8px 12px',
    color: 'var(--color-neutral-800, #1f2937)',
    borderBottom: '1px solid var(--color-neutral-100, #f3f4f6)'
  },
  numericCell: {
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right'
  }
}))

export default function TableCard ({ table }) {
  const classes = useStyles()
  if (!table || !Array.isArray(table.columns) || !Array.isArray(table.rows)) return null

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography component='div' className={classes.title}>{table.title || table.id}</Typography>
        {table.description && (
          <Typography component='div' className={classes.description}>{table.description}</Typography>
        )}
      </div>
      <Table size='small' className={classes.table}>
        <TableHead>
          <TableRow>
            {table.columns.map(col => (
              <TableCell
                key={col.id}
                className={classes.th}
                align={isRightAligned(table.rows, col.id) ? 'right' : 'left'}
              >
                {col.label || col.id}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {table.rows.map((row, rowIdx) => (
            <TableRow key={rowIdx}>
              {table.columns.map(col => (
                <TableCell
                  key={col.id}
                  className={classes.td}
                  align={isRightAligned(table.rows, col.id) ? 'right' : 'left'}
                >
                  {formatCell(row[col.id])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

// Numbers / percent / currency right-align; everything else left-aligns.
function isRightAligned (rows, columnId) {
  for (const row of rows) {
    const v = row[columnId]
    if (v == null) continue
    if (typeof v === 'number') return true
    if (typeof v === 'string' && /^[+\-$]?[\d,.]+%?$/.test(v.trim())) return true
    return false
  }
  return false
}

function formatCell (value) {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

TableCard.propTypes = {
  table: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string
    })),
    rows: PropTypes.arrayOf(PropTypes.object)
  })
}
